import { RotateCcw, Copy, FileText, FileJson, TrendingUp } from 'lucide-react';
import type { ParetoItem, ParetoEngineResults } from '../types';

interface ResultsViewProps {
  items: ParetoItem[];
  engineResults: ParetoEngineResults | null;
  onReset: () => void;
  onRecursivePareto: (topItems: ParetoItem[]) => void;
  recursiveLevel: number;
  /** Share of items in the top band (0–1) — the session's setting. */
  threshold?: number;
}

export function ResultsView({ items, engineResults, onReset, onRecursivePareto, recursiveLevel, threshold = 0.2 }: ResultsViewProps) {
  const topPct = Math.round(threshold * 100);
  const restPct = 100 - topPct;
  const nestedPct = Math.round(threshold * threshold * 1000) / 10;
  const sortedItems = [...items].sort((a, b) => {
    if (a.contribution === null) return 1;
    if (b.contribution === null) return -1;
    return b.contribution - a.contribution;
  });

  // Top-band membership comes from the engine adapter when available; the
  // count-based slice applies the same TOOL_SPEC rule as a sync fallback.
  const topN = Math.ceil(items.length * threshold);
  const top20Percent = engineResults
    ? sortedItems.filter((item) => engineResults.get(item.id)?.isTopBand)
    : sortedItems.slice(0, topN);
  const remaining80Percent = engineResults
    ? sortedItems.filter((item) => !engineResults.get(item.id)?.isTopBand)
    : sortedItems.slice(topN);

  const handleExport = async (format: 'markdown' | 'json' | 'clipboard') => {
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (format) {
      case 'markdown':
        content = `# Pareto Analysis Results${recursiveLevel > 0 ? ` (Recursive Level ${recursiveLevel})` : ''}\n\n`;
        content += `Generated: ${new Date().toLocaleString()}\n\n`;
        content += `## Top ${topPct}% (Highest Contribution)\n\n`;
        top20Percent.forEach((item, index) => {
          content += `${index + 1}. ${item.name} (score: ${item.contribution})\n`;
        });
        content += `\n## Remaining ${restPct}%\n\n`;
        remaining80Percent.forEach((item, index) => {
          content += `${index + 1}. ${item.name} (score: ${item.contribution})\n`;
        });
        filename = `pareto-analysis-${new Date().toISOString().split('T')[0]}.md`;
        mimeType = 'text/markdown';
        break;
      case 'json':
        content = JSON.stringify({ top20Percent, remaining80Percent, allItems: sortedItems }, null, 2);
        filename = `pareto-analysis-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
        break;
      case 'clipboard':
        content = `Top ${topPct}%:\n${top20Percent.map((item, index) => `${index + 1}. ${item.name} (${item.contribution})`).join('\n')}\n\nRemaining ${restPct}%:\n${remaining80Percent.map((item, index) => `${index + 1}. ${item.name} (${item.contribution})`).join('\n')}`;
        await navigator.clipboard.writeText(content);
        alert('Copied to clipboard!');
        return;
    }

    if (format !== 'clipboard') {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Pareto Analysis Results{recursiveLevel > 0 && ` (Level ${recursiveLevel})`}
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          The top {topPct}% of items — the few that carry most of the value
        </p>

        {/* Top band */}
        <div className="mb-6 p-4 bg-[var(--green-dim)] border-2 border-[var(--green)]">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-6 w-6 text-[var(--green)]" />
            <h3 className="text-xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">
              Top {topPct}% (Highest Contribution) - {top20Percent.length} items
            </h3>
          </div>
          <div className="space-y-2">
            {top20Percent.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-[var(--bg-raised)] border border-[var(--green)]"
              >
                <div className="flex items-center gap-3">
                  <span className="font-bold text-[var(--green)] dark:text-[var(--text-ter)] w-8">{index + 1}.</span>
                  <span className="text-[var(--text-pri)] font-medium">{item.name}</span>
                </div>
                <span className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">{item.contribution}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Remaining */}
        <div className="mb-6 p-4 bg-[var(--surface)] border border-[var(--rule)]">
          <h3 className="text-xl font-bold text-[var(--text-pri)] mb-3">
            Remaining {restPct}% - {remaining80Percent.length} items
          </h3>
          <div className="space-y-2">
            {remaining80Percent.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-[var(--bg-raised)] border border-[var(--rule)]"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-[var(--text-sec)] w-8">{index + 1}.</span>
                  <span className="text-[var(--text-sec)]">{item.name}</span>
                </div>
                <span className="text-[var(--text-sec)]">{item.contribution}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recursive Pareto Option */}
        {top20Percent.length > 1 && (
          <div className="mb-6 p-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
            <h3 className="font-semibold text-[var(--text-sec)] mb-2">Recursive Pareto Analysis</h3>
            <p className="text-sm text-[var(--text-sec)] mb-3">
              Apply Pareto analysis again to the top {topPct}% to find the top {nestedPct}% ({topPct}% of {topPct}%).
            </p>
            <button
              onClick={() => onRecursivePareto(top20Percent)}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
            >
              Apply Pareto to Top {topPct}%
            </button>
          </div>
        )}

        {/* Export Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-3">Export</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleExport('markdown')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span className="text-xs">Markdown</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <FileJson className="h-5 w-5" />
              <span className="text-xs">JSON</span>
            </button>
            <button
              onClick={() => handleExport('clipboard')}
              className="flex flex-col items-center gap-2 px-4 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Copy className="h-5 w-5" />
              <span className="text-xs">Clipboard</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}


